// useLiveSession — 單路觀看 session 的精簡 hook(V4P-3 多路同看 grid 用;
// 自 ls-v4 iPad Multi-Window 移植的產品設計)。
//
// 與 DeviceLivePage 同一條 spec 15 §1.1 信令流(W4/APP-02 先驗 grant、
// LOCKED:TURN 由 start_viewing response 派發、pending ICE 緩衝、
// setConfiguration → setLocalDescription → setRemoteDescription 順序),
// 但刻意「薄」:無 stats 面板、無自動重試/斷線寬限(grid 一格失敗點重試
// 即可,不值得四格各跑重試狀態機)。單機全功能觀看仍走 DeviceLivePage。
// platform 面零新依賴:每 device ≤4 viewing sessions(02 §7.2)本就支援
// 多格並行。

import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { signaling, type SignalFrame } from '../../lib/ws';
import { classifyError, extractViewingGrant, type LiveError } from '../devices/DeviceLivePage';

export type GridPhase = 'connecting' | 'streaming' | 'failed' | 'stopped';

const WSS_OPEN_TIMEOUT_MS = 10_000;
// disconnected 寬限:relay 半死時瀏覽器常先 disconnected(不一定升 failed),薄版若不理會
// 會靜止卡死(黑/凍幀、無提示)。給短寬限自癒,逾時仍未恢復 → 當 failed(顯示 retry 鈕,
// 不黑到天荒地老)。仍不做自動重建狀態機(grid 一格薄,走 retry 鈕)。
const GRID_DISCONNECT_GRACE_MS = 4000;

function waitForOpen(): Promise<void> {
  if (signaling.getState() === 'open') return Promise.resolve();
  signaling.connect();
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = signaling.on('state', (s) => {
      if (s === 'open') {
        off();
        if (timer) clearTimeout(timer);
        resolve();
      } else if (s === 'closed') {
        off();
        if (timer) clearTimeout(timer);
        reject(new Error(i18n.t('live.signalingClosed')));
      }
    });
    timer = setTimeout(() => {
      off();
      reject(new Error(i18n.t('live.signalingTimeout')));
    }, WSS_OPEN_TIMEOUT_MS);
  });
}

interface CellRefs {
  gen: number;
  active: boolean;
  pc: RTCPeerConnection | null;
  sessionId: string | null;
  pendingIce: RTCIceCandidateInit[];
  discTimer: ReturnType<typeof setTimeout> | null; // disconnected 寬限計時(逾時未恢復 → failed)
}

export interface LiveSession {
  phase: GridPhase;
  error: LiveError | null;
  retry: () => void;
}

/** 掛載即開看、卸載即 stop_viewing;一格一 session,互不干擾。 */
export function useLiveSession(deviceId: string, videoRef: React.RefObject<HTMLVideoElement | null>): LiveSession {
  const [phase, setPhase] = useState<GridPhase>('connecting');
  const [error, setError] = useState<LiveError | null>(null);
  const r = useRef<CellRefs>({ gen: 0, active: true, pc: null, sessionId: null, pendingIce: [], discTimer: null });

  const sendIce = useCallback(
    (candidate: RTCIceCandidateInit) => {
      signaling.sendRaw({
        type: 'ice_candidate',
        session_id: r.current.sessionId ?? undefined,
        device_id: deviceId,
        candidate,
      } as SignalFrame);
    },
    [deviceId]
  );

  const teardown = useCallback(
    (sendStop: boolean) => {
      const s = r.current;
      if (s.discTimer) {
        clearTimeout(s.discTimer);
        s.discTimer = null;
      }
      if (sendStop && s.sessionId) {
        signaling.request('stop_viewing', { session_id: s.sessionId }, { device_id: deviceId }).catch(() => {});
      }
      if (s.pc) {
        try {
          s.pc.close();
        } catch {
          /* closed */
        }
      }
      s.pc = null;
      s.sessionId = null;
      s.pendingIce = [];
      const v = videoRef.current;
      if (v) v.srcObject = null;
    },
    [deviceId, videoRef]
  );

  const start = useCallback(async () => {
    const s = r.current;
    if (!deviceId || !s.active) return;
    const gen = ++s.gen;
    setError(null);
    setPhase('connecting');
    teardown(false);
    try {
      await waitForOpen();
      if (gen !== s.gen || !s.active) return;
      const pc = new RTCPeerConnection(); // iceServers 留空:TURN 由 response 派發(LOCKED)
      s.pc = pc;
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.ontrack = (ev) => {
        if (r.current.pc !== pc) return;
        const v = videoRef.current;
        if (v) {
          v.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
          void v.play().catch(() => {});
        }
        setPhase('streaming');
      };
      pc.onicecandidate = (ev) => {
        if (r.current.pc !== pc || !ev.candidate) return;
        const cand = ev.candidate.toJSON();
        if (r.current.sessionId) sendIce(cand);
        else r.current.pendingIce.push(cand);
      };
      pc.oniceconnectionstatechange = () => {
        if (r.current.pc !== pc) return;
        const st = pc.iceConnectionState;
        const fail = () => {
          teardown(true);
          setError({ kind: 'ice_failed', title: i18n.t('live.iceFailedTitle'), detail: i18n.t('live.iceFailedDetail') });
          setPhase('failed');
        };
        if (st === 'failed') {
          fail();
        } else if (st === 'disconnected') {
          // 別靜止卡死:短寬限後仍未恢復 → 當 failed(顯示 retry 鈕)。
          if (!r.current.discTimer) {
            r.current.discTimer = setTimeout(() => {
              r.current.discTimer = null;
              const cur = r.current.pc;
              if (cur === pc && (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed')) {
                fail();
              }
            }, GRID_DISCONNECT_GRACE_MS);
          }
        } else if (st === 'connected' || st === 'completed') {
          if (r.current.discTimer) {
            clearTimeout(r.current.discTimer);
            r.current.discTimer = null;
          }
        }
      };
      const offer = await pc.createOffer();
      if (gen !== s.gen || s.pc !== pc) return;
      const resp = await signaling.request('start_viewing', { sdp: offer }, { device_id: deviceId });
      if (gen !== s.gen || s.pc !== pc) return;
      const grant = extractViewingGrant(resp);
      if (grant.sessionId) s.sessionId = grant.sessionId;
      if (grant.iceServers.length > 0) {
        try {
          pc.setConfiguration({ iceServers: grant.iceServers });
        } catch {
          /* host/srflx fallback */
        }
      }
      await pc.setLocalDescription(offer);
      await pc.setRemoteDescription(grant.answer);
      const queued = s.pendingIce.splice(0);
      for (const c of queued) sendIce(c);
    } catch (e) {
      if (gen !== r.current.gen || !r.current.active) return;
      teardown(false);
      setError(classifyError(e));
      setPhase('failed');
    }
  }, [deviceId, sendIce, teardown, videoRef]);

  // 掛載開看/卸載停看 + room 訂閱(ws.ts subscribe 冪等+refcount,與其他訂戶共室)
  useEffect(() => {
    const s = r.current;
    s.active = true;
    if (signaling.getState() === 'open') void signaling.subscribe(deviceId).catch(() => {});
    void start();
    return () => {
      s.active = false;
      s.gen += 1;
      teardown(true);
      void signaling.unsubscribe(deviceId).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // room 重訂閱:掛載時已 open 訂一次(上方 effect);之後僅在 'open' 轉換
  //(初連/重連,server 房籍隨連線消失)由 state listener 重訂,否則重連後此格
  // 會停收 room event 廣播。鏡像 DeviceLivePage,但刻意只訂房——grid 一格薄、
  // 不做媒體自動重建(媒體死了走該格 retry 鈕)。subscribe 冪等+refcount。
  useEffect(() => {
    return signaling.on('state', (sNew) => {
      if (sNew !== 'open') return;
      if (!r.current.active) return;
      void signaling.subscribe(deviceId).catch(() => {});
    });
  }, [deviceId]);

  // 遠端 ICE(session_id 過濾 → 多格並行不互踩)
  useEffect(() => {
    return signaling.on('ice_candidate', (frame) => {
      const s = r.current;
      const pc = s.pc;
      if (!pc) return;
      const fdev = frame.device_id ?? (frame.payload as { device_id?: string } | undefined)?.device_id;
      if (fdev && fdev !== deviceId) return;
      const fsess = frame.session_id ?? (frame.payload as { session_id?: string } | undefined)?.session_id;
      if (fsess && s.sessionId && fsess !== s.sessionId) return;
      const cand = (frame.payload as { candidate?: unknown } | undefined)?.candidate ?? frame['candidate'];
      if (!cand || cand === 'null') return;
      void pc.addIceCandidate(cand as RTCIceCandidateInit).catch(() => {});
    });
  }, [deviceId]);

  return { phase, error, retry: () => void start() };
}
